import React, { FunctionComponent, useContext } from 'react';
import { Box } from '@wix/design-system';
import { Article, ArticleSortType, Category, Label } from '@wix/answers-api';
import { SubHeader } from '../SubHeader/SubHeader';
import { SEO } from '../SEO';
import { PAGES } from '../../constants';
import {
  PageType,
  PageSeo,
  RoadmapFilterType,
  RoadmapFeatureResolution,
  ArticleSearchResult,
} from '../../types';
import { UserEngagement } from '../UserEngagement';
import { AgentToolbar } from '../AgentToolbar';
import { Context } from '../../context';
import { TBears } from '../3Bears';
import ABvisibleElement from '../ABvisibleElement';
import { isRoadmapArticle } from '../../utils/roadmap';
import { PageView } from '../PageView';
import css from './index.module.scss';

export type PageData = {
  article?: Article;
  category?: Category;
  roadmapData?: RoadmapPageData;
  searchResults?: ArticleSearchResult;
};

export type RoadmapPageData = {
  sortType?: ArticleSortType;
  filterType?: RoadmapFilterType;
  resolution?: RoadmapFeatureResolution;
  roadmapLabels?: Label[];
  label?: { name: string; id: string };
};

export type PageTemplateData = {
  subHeaderLabels?: string[];
  pageData?: PageData;
  itemId?: string;
  itemType?: string;
  hasError?: boolean;
};

type PageTemplateProps = {
  children: React.ReactNode;
  pageType: PageType;
  pageSeo: PageSeo;
};

export const PageTemplate: FunctionComponent<PageTemplateProps> = ({
  children,
  pageType,
  pageSeo,
}) => {
  const { isMobile, isTablet, pageTemplateData } = useContext(Context);

  const {
    pageData = {},
    subHeaderLabels,
    itemId,
    itemType,
    hasError = false,
  } = pageTemplateData;

  const isRoadmapArticlePage =
    pageData.article &&
    pageData.article.resolution &&
    pageData.article.labels &&
    pageData.roadmapData?.roadmapLabels &&
    isRoadmapArticle(
      pageData.article.resolution,
      pageData.article.labels,
      pageData.roadmapData?.roadmapLabels
    );

  const hideSubHeaderForMobileRoadmap =
    (pageType === PAGES.ROADMAP ||
      (pageType === PAGES.ARTICLE && isRoadmapArticlePage)) &&
    (isMobile || isTablet);

  return (
    <Box direction="vertical" className={css.wrapper}>
      <SEO pageData={pageData} pageType={pageType} pageSeo={pageSeo} />
      <TBears />
      <AgentToolbar pageData={pageData} pageType={pageType} />
      {pageType !== PAGES.HOMEPAGE &&
        !hasError &&
        !hideSubHeaderForMobileRoadmap && (
          <SubHeader
            pageType={pageType}
            labels={subHeaderLabels || []}
            itemId={itemId}
            itemType={itemType}
            isRoadmapArticlePage={isRoadmapArticlePage}
          />
        )}
      <ABvisibleElement pageType={pageType} />
      <UserEngagement
        pageData={pageData}
        pageType={pageType}
        itemType={itemType as string}
        itemId={itemId}
      />
      <PageView pageData={pageData} pageType={pageType} itemId={itemId} />
      {children}
    </Box>
  );
};
